/******************************************************************************\
 * :$
 *
 * Copyright(c) 2021 SAS Institute Inc., Cary, NC, USA. All Rights Reserved.
 *
 * Name: Submission
 *
 * Purpose: Display the submission info/data
 *
 * Author: craig (Craig.Simpson@sas.com)
 *
 * Support: SAS(r) Solutions OnDemand
 *
 * Input:
 *
 * Output:
 *
 * Parameters: (if applicable)
 *
 * Dependencies/Assumptions:
 *
 * Usage:
 *
 * History:
 * ddmmmyyyy userid description (Change Code)
 * 11Nov2021 craig File created.
 \******************************************************************************/
import React, {Fragment, useEffect, useMemo, useRef, useState} from "react";
import {GSContext} from "../../../context/context";
import {Col, Container, Nav, Navbar, Row} from "react-bootstrap";
import {NavLink, useParams} from "react-router-dom";
import {IGoldStandardPillar} from "../../../models/application/IApplication";
import "./Submission.css";
import {useRequest} from "../../../helper/useRequest";
import {SubmissionPillar} from "./SubmissionPillar";

export const Submission: React.FC = (prop) => {
    const {userID, teamID, submissionID, activePillar} = useParams();

    const navElement = useRef<HTMLDivElement>(null);

    const [sessionUser] = React.useContext(GSContext);
    const [submissionData, setSubmissionData] = useState<IGoldStandardPillar>();
    const [disabled, setDisabled] = useState<boolean>(false);
    const appDataURL = process.env.REACT_APP_API_URL + "/api/submission/" + userID + "/" + teamID + "/" + submissionID + "/" + activePillar;


    const [requestState, getSubmissionData] = useRequest({url: appDataURL, initialIsLoading: true,})

    const {isLoading, data, error} = requestState;

    /**
     * call the backend to get the submission data
     */
    useMemo(() => {
        getSubmissionData();
        let tmpUserID:string = userID!
        if (sessionUser.UserID !== parseInt(tmpUserID)) {
            setDisabled(true);
        }
    }, [])

    /**
     * if submission data changes, set it
     */
    useEffect(() => {
            if (data != null) {
                setSubmissionData(data);
                const today = new Date();


                if (userID === undefined) {
                    setDisabled(true)
                }
                else if (parseInt(userID) !== sessionUser.UserID) {
                    setDisabled(true);
                } else if (data.TeamSubmissionUserLinkage.Submitted) {
                    setDisabled(true);
                } else if (today <= new Date(data.Team.EndDate) && today >= new Date(data.Team.StartDate)) {
                    setDisabled(false);
                } else {
                    setDisabled(true);
                }
            }
        }, [data]
    )


    /**
     * build the pillar navigation
     */
    const pillarNav = () => {
        const pillarURL = "/submission/" + userID + "/" + teamID + "/" + submissionID + "/";

        return <Navbar className={"pillar-nav"}>
            <Container>
                <Nav ref={navElement} className="flex-column">
                    <NavLink id={"P1"}
                             className={({isActive}) => (isActive ? 'pillar-nav-link active' : 'pillar-nav-link')}
                             to={pillarURL + "P1"}>P1</NavLink>
                    <NavLink id={"P2"}
                             className={({isActive}) => (isActive ? 'pillar-nav-link active' : 'pillar-nav-link')}
                             to={pillarURL + "P2"}>P2</NavLink>
                    <NavLink id={"P3"}
                             className={({isActive}) => (isActive ? 'pillar-nav-link active' : 'pillar-nav-link')}
                             to={pillarURL + "P3"}>P3/4</NavLink>
                    <NavLink id={"P5"}
                             className={({isActive}) => (isActive ? 'pillar-nav-link active' : 'pillar-nav-link')}
                             to={pillarURL + "P5"}>P5</NavLink>
                    <NavLink id={"Essay"}
                             className={({isActive}) => (isActive ? 'pillar-nav-link active' : 'pillar-nav-link')}
                             to={pillarURL + "ESSAY"}>Essay</NavLink>
                    <NavLink id={"Comments"}
                             className={({isActive}) => (isActive ? 'pillar-nav-link active' : 'pillar-nav-link')}
                             to={pillarURL + "COMMENTS"}>Comments</NavLink>
                </Nav>
            </Container>
        </Navbar>
    }


    const PillarSection = () => {
        if (submissionData !== null && submissionData !== undefined) {
            return <SubmissionPillar pillarData={submissionData!}
                                     disabled={disabled}/>
        } else {
            return <></>
        }
    }

    const buildSubmissionData = () => {
        //TODO - check that ResponseList[0] is not undefined or empty
        let name = submissionData?.Submission?.OrgDataMap["GS_Sig_Name_FIRST_NAME"]?.ResponseList[0].Response + " " + submissionData?.Submission?.OrgDataMap["GS_Sig_Name_LAST_NAME"]?.ResponseList[0].Response;
        let empCount:string = submissionData?.Submission?.OrgDataMap["Approx_US_Employee_Count"]?.ResponseList[0].Response;
        if (!isNaN(Number(empCount))) {
            empCount = Number(empCount).toLocaleString();
        }
        if (submissionData?.Submission?.OrgDataMap["GS_Sig_Name_CREDENTIALS"]?.ResponseList[0].Response !== undefined) {
            name += ", " + submissionData?.Submission?.OrgDataMap["GS_Sig_Name_CREDENTIALS"]?.ResponseList[0].Response;
        }
        return <Container id={"body"}>
            <Row className={"header-container"}>
                <Col>
                    <Container className={"submission-header-container"}>
                        <Row className={"submission-info-header"}>
                            <Col>
                                <Container>
                                    <Row>{submissionData?.Submission?.Organization}</Row>
                                    <Row>
                                        {empCount} Employees
                                    </Row>
                                    <Row>
                                        {submissionData?.Submission?.OrgDataMap["Approx_US_Locations"]?.ResponseList[0].Response} Location(s)
                                    </Row>
                                </Container>
                            </Col>
                            <Col>
                                <Container>
                                    <Row>{name}</Row>
                                    <Row>
                                        {submissionData?.Submission?.OrgDataMap["GS_Sig_Name_TITLE"]?.ResponseList[0].Response}
                                    </Row>
                                    <Row></Row>
                                </Container>
                            </Col>
                            <Col>
                                <Container>
                                    <Row>{submissionData?.Submission?.OrgDataMap["NAICS_Code_1"]?.ResponseList[0]?.Response}</Row>
                                    <Row>{submissionData?.Submission?.OrgDataMap["NAICS_Code_2"]?.ResponseList[0]?.Response}</Row>
                                    <Row>{submissionData?.Submission?.OrgDataMap["NAICS_Code_3"]?.ResponseList[0]?.Response}</Row>
                                </Container>
                            </Col>
                        </Row>
                    </Container>
                </Col>
            </Row>
            <Row>
                <Col className={"pillar-nav"}>
                    <Container>
                        {pillarNav()}
                    </Container>
                </Col>
                <Col className={"pillar-question"}>
                    <Row>
                        {PillarSection()}
                    </Row>
                </Col>
            </Row>

        </Container>

    }


    return (
        <Fragment>
            {isLoading && <p>Loading ...</p>}
            {error && <p>Error</p>}
            {data && buildSubmissionData()}
        </Fragment>
    );
}